<template>
  <div class="rm-drawer-step">
    <div class="rm-drawer-scroll">
      <section class="drawer-section">
        <h3 class="drawer-section-title">
          {{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.SUB_TITLE') }}
        </h3>
        <v-radio-group v-model="selectedOffer" class="repairer-choice-group" hide-details>
          <template
            v-for="offer of currentRepairFile?.repairer_estimate?.repairer_estimate_option_list"
            :key="offer.technical_id"
          >
            <v-card-secondary
              class="repairer-choice-card pa-3"
              :class="{
                'secondary-card--selected': selectedOffer?.technical_id === offer.technical_id
              }"
              @click="selectedOffer = offer"
            >
              <v-radio :value="offer" hide-details>
                <template #label>
                  <div class="secondary-card-row w-100">
                    <p class="drawer-offer-label">
                      {{ $t('COMMON.PRODUCT_END_STATUS.' + offer.offer_type.toUpperCase()) }}
                    </p>
                    <p class="ml-auto drawer-offer-price">
                      {{ Number(offer.total_cost).toFixed(2) }} {{ $t('COMMON.EURO') }}
                    </p>
                  </div>
                </template>
              </v-radio>

              <div
                v-if="
                  selectedOffer?.technical_id === offer.technical_id &&
                  !initialZeroCostIds.includes(offer.technical_id)
                "
                class="repairer-offer-detail"
              >
                <div class="repairer-offer-detail-content">
                  <div class="repairer-choice-metrics">
                    <div class="secondary-card--soft repairer-choice-metric">
                      <p class="repairer-choice-metric-label">
                        {{
                          $t(
                            isReplacementEstimateOffer(offer)
                              ? 'COMMON.COST'
                              : 'ESTIMATE_REPAIRER_CLIENT_CHOICE.TRANSPORT_COST'
                          )
                        }}
                      </p>
                      <p class="repairer-choice-metric-value">
                        {{ repairerCost(offer).toFixed(2) }} {{ $t('COMMON.EURO') }}
                      </p>
                    </div>
                    <div class="secondary-card--soft repairer-choice-metric">
                      <p class="repairer-choice-metric-label">
                        {{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.MARGIN') }}
                      </p>
                      <p class="repairer-choice-metric-value">
                        {{ offer.margin }} {{ $t('COMMON.PERCENT') }}
                      </p>
                    </div>
                  </div>
                  <div v-if="!isEditingOffer(offer)" class="drawer-actions repairer-offer-actions">
                    <v-btn-icon
                      :title="$t('ESTIMATE_REPAIRER_CLIENT_CHOICE.MODIFICATION_PRICE')"
                      icon="mdi-pencil-outline"
                      size="small"
                      @click.stop="startEdit(offer, 'price')"
                    />
                    <v-btn-icon
                      :title="$t('ESTIMATE_REPAIRER_CLIENT_CHOICE.MODIFICATION_MARGIN')"
                      icon="mdi-percent-outline"
                      size="small"
                      @click.stop="startEdit(offer, 'margin')"
                    />
                  </div>
                </div>
              </div>
            </v-card-secondary>

            <v-form-card
              v-if="isEditingOffer(offer)"
              v-model="editFormValid"
              @submit.prevent="saveEditedOffer(offer)"
            >
              <div class="drawer-form-header">
                <p class="drawer-form-title">{{ editLabel }}</p>
                <v-btn-icon
                  class="drawer-form-close"
                  :aria-label="$t('COMMON.BUTTON.CLOSE')"
                  :title="$t('COMMON.BUTTON.CLOSE')"
                  icon="mdi-close"
                  size="small"
                  @click="cancelEdit"
                />
              </div>
              <v-text-field
                v-model.number="editingValue"
                type="number"
                :rules="editingField === 'price' ? priceRules : marginRules"
                hide-details="auto"
              >
                <template #append-inner>
                  <span class="drawer-field-suffix">
                    {{ editingField === 'price' ? $t('COMMON.EURO') : $t('COMMON.PERCENT') }}
                  </span>
                </template>
              </v-text-field>
              <v-btn-secondary
                class="w-100"
                :disabled="!editFormValid || loading"
                :loading="loading"
                prepend-icon="mdi-check"
                variant="tonal"
                type="submit"
              >
                {{ editSaveLabel }}
              </v-btn-secondary>
            </v-form-card>
          </template>
        </v-radio-group>
      </section>

      <section class="drawer-section">
        <h3 class="drawer-section-title">
          {{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.SEND_ESTIMATE_AGAIN') }}
        </h3>
        <v-form v-model="emailFormValid" class="drawer-email-form">
          <v-text-field
            v-model="email"
            required
            :rules="[...requiredRules, ...emailRules]"
            hide-details="auto"
            prepend-inner-icon="mdi-email-outline"
          />
          <v-btn-secondary
            class="w-100"
            :disabled="loading || !emailFormValid || hasSelectedEstimateOption"
            :loading="loading"
            prepend-icon="mdi-send-outline"
            variant="tonal"
            @click="sendClientEmail"
          >
            {{ $t('ESTIMATE_REPAIRER_CLIENT_CHOICE.BUTTON.SEND_EMAIL') }}
          </v-btn-secondary>
        </v-form>
      </section>
    </div>
  </div>
</template>

<script>
import Validators from '@/utils/validators'
import { isReplacementEstimateOffer } from '@/utils/repairFileUtils'
import RepairFileService from '@/services/RepairFileService'
import { displayToast, handleResponseError } from '@/utils/toastUtils'

export default {
  props: { currentRepairFile: Object },
  data() {
    return {
      loading: false,
      editFormValid: false,
      selectedOffer: null,
      editingOfferId: null,
      editingField: null,
      editingValue: null,
      initialZeroCostIds: [],
      priceRules: Validators.numberRules,
      marginRules: Validators.marginRules,
      requiredRules: Validators.requiredRules,
      emailRules: Validators.emailRules,
      email: null,
      emailFormValid: false
    }
  },
  computed: {
    footerState() {
      return { disabled: this.loading || !this.selectedOffer, loading: this.loading }
    },
    hasSelectedEstimateOption() {
      return this.currentRepairFile?.repairer_estimate?.repairer_estimate_option_list?.some(
        (option) => option.is_selected === true
      )
    },
    editLabel() {
      return this.$t(
        this.editingField === 'price'
          ? 'ESTIMATE_REPAIRER_CLIENT_CHOICE.MODIFICATION_PRICE'
          : 'ESTIMATE_REPAIRER_CLIENT_CHOICE.MODIFICATION_MARGIN'
      )
    },
    editSaveLabel() {
      return this.$t(
        this.editingField === 'price'
          ? 'ESTIMATE_REPAIRER_CLIENT_CHOICE.SAVE_PRICE'
          : 'ESTIMATE_REPAIRER_CLIENT_CHOICE.SAVE_MARGIN'
      )
    }
  },
  created() {
    this.email = this.currentRepairFile?.email
    const options = this.currentRepairFile.repairer_estimate.repairer_estimate_option_list
    this.initialZeroCostIds = options
      .filter((option) => Number(option.total_cost) === 0)
      .map((option) => option.technical_id)
  },
  mounted() {
    this.emitFooterState()
  },
  watch: {
    footerState: {
      handler() {
        this.emitFooterState()
      },
      deep: true
    }
  },
  methods: {
    isReplacementEstimateOffer,
    emitFooterState() {
      this.$emit('updateFooterState', this.footerState)
    },
    submit() {
      this.selectedOffer.is_selected = true
      this.$emit('openEstimateRepairerClientChoiceValidation')
    },
    repairerCost(offer) {
      return offer.estimate_cost + offer.transport_cost + offer.labor_cost
    },
    isEditingOffer(offer) {
      return this.editingOfferId === offer.technical_id
    },
    startEdit(offer, field) {
      this.editingOfferId = offer.technical_id
      this.editingField = field
      this.editingValue = Number(field === 'price' ? offer.total_cost : offer.margin)
      this.editFormValid = false
    },
    cancelEdit() {
      this.editingOfferId = null
      this.editingField = null
      this.editingValue = null
      this.editFormValid = false
    },
    saveEditedOffer(offer) {
      if (!this.editFormValid) return
      if (this.editingField === 'price') {
        offer.total_cost = Number(this.editingValue)
        offer.margin = (
          (100 * (offer.total_cost - this.repairerCost(offer))) /
          offer.total_cost
        ).toFixed(0)
      } else {
        offer.margin = Number(this.editingValue)
        offer.total_cost = this.repairerCost(offer) / (1 - offer.margin / 100)
      }
      this.cancelEdit()
    },
    async sendClientEmail() {
      if (this.hasSelectedEstimateOption) {
        displayToast(this.$t('COMMON.NOT_AUTHORIZED_ACTION_MESSAGE'), 'warning')
        return
      }
      this.loading = true
      try {
        await RepairFileService.sendNewRepairFileRepairerEstimateToClient({
          repair_file_id: this.currentRepairFile.technical_id,
          contact_email: this.email
        })
        displayToast(this.$t('ESTIMATE_REPAIRER_DETAIL.MODAL.SEND_CLIENT_EMAIL'), 'success')
        this.$emit('updateCurrentRepairFile')
        this.$emit('closeNewAction')
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.repairer-choice-group {
  width: 100%;
}
.repairer-choice-group :deep(.v-selection-control-group) {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.repairer-choice-card :deep(.v-selection-control) {
  align-items: center;
  min-height: 32px;
}
.repairer-choice-card :deep(.v-label) {
  opacity: 1;
  width: 100%;
}
.repairer-offer-actions {
  flex-wrap: nowrap;
  padding-inline: 12px;
}
</style>
